// PATH: src/components/documents/ChapterInfoTab.tsx
import * as React from "react";
import {
  Alert,
  Box,
  Button,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import type { Document, Track } from "../../models/domain";
import { useDocumentsApi } from "../../api/documents/documents";
import { buildDocumentPutPayload } from "./documentChapter";
import DocumentChapterBlock from "./DocumentChapterBlock";
import { errorMessage, useToast } from "../ui";

/** Langues proposées ; la langue du manifeste (souvent en minuscules) est gardée telle quelle. */
const LANG_OPTIONS = ["fr", "ar", "en"];
const LANG_NAMES: Record<string, string> = { fr: "Français", ar: "Arabe", en: "Anglais" };
const langLabel = (code: string) => {
  const name = LANG_NAMES[code.toLowerCase()];
  return name ? `${name} (${code})` : code;
};

interface Props {
  document: Document;
  tracks: Track[];
  /** Document enregistré (titre, état, identité…) : la liste doit suivre. */
  onSaved: (doc: Document) => void;
}

interface MetaForm {
  title: string;
  description: string;
  language: string;
}

const formFrom = (d: Document): MetaForm => ({
  title: d.title ?? "",
  description: d.description ?? "",
  language: (d.language as string) || "fr",
});

/**
 * Onglet « Informations » : titre, description, langue (PUT du
 * document) et, à part, l'identité du chapitre (PATCH dédié).
 */
export const ChapterInfoTab: React.FC<Props> = ({ document, tracks, onSaved }) => {
  const { getDocument, updateDocument } = useDocumentsApi();
  const toast = useToast();

  const [form, setForm] = React.useState<MetaForm>(() => formFrom(document));
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    setForm(formFrom(document));
    setError(null);
  }, [document]);

  const initial = formFrom(document);
  const dirty =
    form.title !== initial.title ||
    form.description !== initial.description ||
    form.language !== initial.language;

  const handleSave = async () => {
    if (!form.title.trim()) {
      setError("Le titre est obligatoire.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      /* Le PUT réécrit toutes les colonnes du document : on part de sa version
         serveur (pas de la ligne de la liste) et on n'y met JAMAIS les champs
         de chapitre. */
      const { data: current } = await getDocument(document.id);
      const payload = buildDocumentPutPayload(current, {
        title: form.title.trim(),
        description: form.description.trim() || null,
        language: form.language,
      });
      const { data } = await updateDocument(document.id, payload);
      toast.success("Chapitre enregistré.");
      onSaved({ ...document, ...current, ...data });
    } catch (e) {
      setError(errorMessage(e, "Échec de l'enregistrement."));
    } finally {
      setSaving(false);
    }
  };

  const languages = [
    ...(form.language && !LANG_OPTIONS.includes(form.language) ? [form.language] : []),
    ...LANG_OPTIONS.filter((c) => c.toLowerCase() !== form.language.toLowerCase() || c === form.language),
  ];

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: 2.5 }}>
        <Typography variant="subtitle1" sx={{ mb: 2 }}>
          Informations
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Stack spacing={2}>
          <TextField
            label="Titre"
            required
            value={form.title}
            onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
            fullWidth
            inputProps={{ dir: "auto" }}
          />
          <TextField
            label="Description"
            value={form.description}
            onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
            fullWidth
            multiline
            minRows={3}
            inputProps={{ dir: "auto" }}
          />
          <TextField
            select
            label="Langue"
            value={form.language}
            onChange={(e) => setForm((p) => ({ ...p, language: e.target.value }))}
            sx={{ maxWidth: 200 }}
          >
            {/* Un chapitre importé porte la langue de son manifeste, qui peut
                ne pas être dans la liste : on la garde. */}
            {languages.map((code) => (
              <MenuItem key={code} value={code}>
                {langLabel(code)}
              </MenuItem>
            ))}
          </TextField>
          {/* L'ouverture aux élèves n'est pas ici : un seul interrupteur, avec sa
              confirmation, dans la liste des chapitres (colonne État). Le PUT
              part de la version serveur et garde donc is_active tel quel. */}
          <Box display="flex" justifyContent="flex-end" gap={1}>
            {dirty && (
              <Button color="inherit" onClick={() => setForm(initial)} disabled={saving}>
                Annuler
              </Button>
            )}
            <Button variant="contained" onClick={() => void handleSave()} disabled={saving || !dirty}>
              {saving ? "Enregistrement…" : "Enregistrer"}
            </Button>
          </Box>
        </Stack>
      </Paper>

      <DocumentChapterBlock document={document} tracks={tracks} onSaved={onSaved} />
    </Stack>
  );
};

export default ChapterInfoTab;
